import { SharedAngular } from '@Client/@types/sharedAngular';
import ISettingDetail from '@Shared.Angular/@types/core/contracts/config/ISettingDetail';
import IBusinessDetail from '@Shared.Angular/@types/core/contracts/queryModel/business/businessDetail';
import { Guid, guidsAreEqual } from '@Shared.Angular/@types/guid';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';
import angular, { IScope } from 'angular';
import { IStateProvider } from 'angular-ui-router';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

// Register the application's routes configuration.
angular.module('flowingly.runner.maintenance').config(config);

// Inject the dependencies.
config.$inject = ['$stateProvider'];

function config($stateProvider: IStateProvider) {
  $stateProvider.state(STATE_MAPPINGS.maintenanceSettings, {
    url: '/settings',
    params: { title: 'Maintenance - Settings' },
    views: {
      childcontent: {
        templateUrl:
          'Client/runner.maintenance/settings/runner.maintenance.settings.tmpl.html',
        controllerAs: '$ctrl',
        controller: [
          'maintenanceService',
          'dialogService',
          '$scope',
          'permissionsService',
          'sessionService',
          '$q',
          function (
            maintenanceService: MaintenanceService,
            dialogService: SharedAngular.DialogService,
            $scope: IScope,
            permissionsService: SharedAngular.PermissionsService,
            sessionService: SharedAngular.SessionService,
            $q: angular.IQService
          ) {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const $ctrl = this;

            const currentUsersBusinessId = sessionService.getBusinessId();
            const canChangeAllSettings =
              permissionsService.currentUserHasAnyOfPermissions([
                FlowinglyPermissions.ADMIN_ACTIONS,
                FlowinglyPermissions.ADMIN_ACTIONS_BUSINESS_SETTING
              ]);
            const canChangeSelfSettings =
              permissionsService.currentUserHasPermission(
                FlowinglyPermissions.ADMIN_ACTIONS_BUSINESS_SETTING_SELF
              );
            $ctrl.selectedBusinessName;
            $ctrl.selectedBusinessId;
            $ctrl.canChangeSettings = false;
            $ctrl.updateSetting = updateSetting;
            $ctrl.loadBusinessSettings = loadBusinessSettings;
            $ctrl.populateSettingInputs = populateSettingInputs;
            $ctrl.clearCachedSettings = clearCachedSettings;

            const scrollOptions: ScrollIntoViewOptions = {
              behavior: 'smooth',
              block: 'center'
            };

            $scope.$parent.$watch(
              '$ctrl.selectedBusiness',
              (business: IBusinessDetail) => {
                $ctrl.selectedBusinessId = business?.id;
                $ctrl.selectedBusinessName = business?.name;
                $ctrl.canChangeSettings =
                  canChangeAllSettings ||
                  (canChangeSelfSettings &&
                    (guidsAreEqual(business?.id, currentUsersBusinessId) ||
                      guidsAreEqual(
                        business?.parentId,
                        currentUsersBusinessId
                      )));
                loadBusinessSettings($ctrl.selectedBusinessId);
              }
            );

            // Private Methods
            function loadBusinessSettings(businessId: Guid) {
              $ctrl.addSettingName = '';
              $ctrl.addSettingValue = '';
              $ctrl.businessSettings = [];
              if (!businessId) {
                return;
              }
              const promises = [
                maintenanceService.getBusinessSettings(businessId),
                maintenanceService.getApplicationSettings()
              ];
              return $q.all(promises).then((results) => {
                const businessSettings = results[0];
                const applicationSettings = results[1] as ISettingDetail[];
                const appSettingsObject: { [key: string]: ISettingDetail } = {};
                applicationSettings.forEach((setting) => {
                  appSettingsObject[setting.name] = setting;
                });
                if ($ctrl.selectedBusinessId !== businessId) {
                  return;
                }
                $ctrl.businessSettings = Object.keys(businessSettings)
                  .map((key) => {
                    return {
                      name: key,
                      value: businessSettings[key],
                      description: appSettingsObject[key]?.comments
                    };
                  })
                  .sort((a, b) => {
                    return a.name.localeCompare(b.name);
                  });
              });
            }

            function updateSetting(
              businessId: Guid,
              settingName: string,
              settingValue: string
            ) {
              maintenanceService
                .setBusinessSetting(businessId, settingName, settingValue)
                .then(
                  () => {
                    $ctrl.loadBusinessSettings(businessId).then(() => {
                      $ctrl.businessSettings.find(
                        (setting) => setting.name === settingName
                      ).updated = true;

                      setTimeout(() => {
                        const settings =
                          document.getElementsByClassName('setting-name');
                        Array.from(settings)
                          .find((x) => x.textContent === settingName)
                          .scrollIntoView(scrollOptions);
                      }, 300);
                    });
                  },
                  () => {
                    const options = {
                      headerText: 'Failed to update setting',
                      message: `Error while trying to set "${settingName}" to "${settingValue}"`
                    };
                    dialogService.showMessageDialog(options);
                  }
                );
            }

            function clearCachedSettings() {
              maintenanceService.clearCachedSettings().then(
                () => {
                  $ctrl.loadBusinessSettings($ctrl.selectedBusinessId);
                },
                () => {
                  const options = {
                    headerText: 'Failed to clear caches',
                    message: 'Error while clearing setting caches'
                  };
                  dialogService.showMessageDialog(options);
                }
              );
            }

            function populateSettingInputs(name: string, value: string) {
              $ctrl.addSettingName = name;
              $ctrl.addSettingValue = value;
              const settingValueInput = document.getElementById(
                'addSettingValue'
              ) as HTMLInputElement;
              settingValueInput.scrollIntoView(scrollOptions);
              settingValueInput.select();
            }
          }
        ]
      }
    }
  });
}
